import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Search, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import IpeToolbarSelect from '@/pages/components/IpeToolbarSelect';
import {
  DEFAULT_TITLE_PRESET_SCOPE,
  TITLE_PRESET_CATEGORIES,
  filterTitlePresetsByCategory,
  filterTitlePresetsByKeyword,
  filterTitlePresetsByScope,
  getTitlePresetCategoryLabel,
  inferTitlePresetCategory,
  resolveDefaultTitlePresetScope,
  resolveVisibleTitlePresetScopes,
} from '@/pages/ipe/workbench/constants/titlePresetCatalog';
import {
  COPYWRITING_TEMPLATE_PLATFORM_OPTIONS,
  getCopywritingTemplatePlatformLabel,
} from '@/pages/ipe/workbench/constants/aiCopywritingTemplateConfig';
import { useIpeWorkspaceMode } from '@/pages/ipe/workbench/hooks/useIpeWorkspaceMode';
import {
  AI_COPYWRITING_LANGUAGE_OPTIONS,
  AI_COPYWRITING_MAX_CHARS_LIMIT,
  DEFAULT_AI_COPYWRITING_LANGUAGE,
  normalizeCopywritingMaxChars,
  sanitizeCopywritingMaxCharsInput,
} from '@/pages/ipe/workbench/constants/aiCopywritingConfig';
import {
  AI_TOOL_STUDIO_PROMPT_MAX_LENGTH,
  getAiToolStudioConfig,
} from '@/pages/ipe/workbench/constants/aiStudioConfig';
import { resolveCopywritingTemplateOwnerLabel } from '@/pages/ipe/workbench/utils/aiCopywritingTemplateLoader';
import DetailToolBridgeSourcesPreview from '@/pages/ipe/workbench/components/DetailToolBridgeSourcesPreview';
import IpeCreditCostHint from '@/components/ipe/IpeCreditCostHint';
import '@/styles/ipe/workbench/Img2ImgPage.css';
import '@/styles/ipe/workbench/modals/AiCopywritingTemplatePickerModal.css';

const UPLOAD_MODE_DEFAULT = 'default';
const UPLOAD_MODE_CUSTOM = 'custom';

function buildCategoryFilterOptions(t) {
  return TITLE_PRESET_CATEGORIES.map((item) => ({
    value: item.key,
    label: item.key === 'all' ? t('aiCwTemplatePicker.allCategories') : getTitlePresetCategoryLabel(item.key),
  }));
}

const LANGUAGE_SELECT_OPTIONS = AI_COPYWRITING_LANGUAGE_OPTIONS.map((opt) => ({
  value: opt.value,
  label: opt.label,
}));

function buildPlatformFilterOptions(t) {
  return [
    { value: 'all', label: t('aiCwTemplatePicker.allShops') },
    ...COPYWRITING_TEMPLATE_PLATFORM_OPTIONS.map((opt) => ({
      value: opt.value,
      label: opt.label,
    })),
  ];
}

function filterTitlePresetsByPlatform(presets, platformKey = 'all') {
  if (!platformKey || platformKey === 'all') return presets;
  const key = String(platformKey).trim().toLowerCase();
  return presets.filter((p) => String(p.platform || '').trim().toLowerCase() === key);
}

/** @deprecated 使用 {@link normalizeCopywritingMaxChars} */
export function parseCopywritingMaxChars(raw) {
  const text = String(raw ?? '').trim();
  if (!text) return undefined;
  return normalizeCopywritingMaxChars(text);
}

function TemplatePickCard({
  preset,
  active,
  disabled,
  onSelect,
  currentUserId,
  ownerNameByUserId,
}) {
  const { t } = useTranslation('workbench');
  const categoryLabel = getTitlePresetCategoryLabel(
    preset.category || inferTitlePresetCategory(preset),
  );
  const platformLabel = getCopywritingTemplatePlatformLabel(preset.platform);
  const ownerLabel = resolveCopywritingTemplateOwnerLabel(preset, {
    currentUserId,
    ownerNameByUserId,
  });
  const scopeLabel = preset.scope === 'personal'
    ? t('aiCwTemplatePicker.scopePersonal')
    : preset.scope === 'team'
      ? t('aiCwTemplatePicker.scopeTeam')
      : t('aiCwTemplatePicker.scopeOfficial');

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      className={`ai-cw-tpl-picker__card${active ? ' is-active' : ''}${disabled ? ' is-disabled' : ''}`}
      aria-pressed={active}
      title={preset.content}
      onClick={() => {
        if (!disabled) onSelect?.(preset);
      }}
      onKeyDown={(event) => {
        if (disabled) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect?.(preset);
        }
      }}
    >
      <div className="ai-cw-tpl-picker__card-top">
        <div className="ai-cw-tpl-picker__card-title">{preset.name}</div>
        <span className={`ai-cw-tpl-picker__badge ai-cw-tpl-picker__badge--${preset.scope || 'official'}`}>
          {scopeLabel}
        </span>
      </div>
      <div className="ai-cw-tpl-picker__card-meta">
        {categoryLabel ? <span className="ai-cw-tpl-picker__chip">{categoryLabel}</span> : null}
        {platformLabel ? (
          <span className="ai-cw-tpl-picker__chip ai-cw-tpl-picker__chip--accent">{platformLabel}</span>
        ) : null}
      </div>
      <div className="ai-cw-tpl-picker__card-body">{preset.content}</div>
      {ownerLabel ? (
        <div className="ai-cw-tpl-picker__card-foot">{t('aiCwTemplatePicker.createdBy', { name: ownerLabel })}</div>
      ) : null}
    </div>
  );
}

/**
 * AI 文案详情联动：选择个人 / 团队 / 官方模板（卡片网格 + 确认后带入功能页）。
 */
export default function AiCopywritingTemplatePickerModal({
  isOpen,
  onClose,
  onConfirm,
  presets = [],
  selectedId = '',
  onSelectId,
  sources = [],
  selectedCount = 0,
  confirming = false,
  canAccessTeamPresets = false,
  currentUserId = '',
  ownerNameByUserId = null,
  initialScope = DEFAULT_TITLE_PRESET_SCOPE,
  initialLanguage = DEFAULT_AI_COPYWRITING_LANGUAGE,
  initialMaxChars = '',
  initialPrompt = '',
  intent = 'create',
  onGoManage,
  /** true：本页直接生成标题（商品库批次详情等），文案改为「确认生成」 */
  generateOnConfirm = false,
}) {
  const { t } = useTranslation('workbench');
  const studioConfig = useMemo(() => getAiToolStudioConfig('aiCopywriting'), []);
  const categoryFilterOptions = useMemo(() => buildCategoryFilterOptions(t), [t]);
  const platformFilterOptions = useMemo(() => buildPlatformFilterOptions(t), [t]);
  const workspaceModeFromHook = useIpeWorkspaceMode();
  const isRemake = intent === 'remake';
  const willGenerateHere = isRemake || generateOnConfirm;
  const promptMaxLength = studioConfig?.promptMaxLength || AI_TOOL_STUDIO_PROMPT_MAX_LENGTH;
  const promptLabel = studioConfig?.promptLabel || t('aiCwTemplatePicker.defaultPromptLabel');
  const promptPlaceholder = studioConfig?.promptPlaceholder || '';

  const visibleScopes = useMemo(
    () => resolveVisibleTitlePresetScopes(workspaceModeFromHook, canAccessTeamPresets),
    [workspaceModeFromHook, canAccessTeamPresets],
  );
  const [scope, setScope] = useState(initialScope);
  const [keyword, setKeyword] = useState('');
  const [category, setCategory] = useState('all');
  const [platform, setPlatform] = useState('all');
  const [language, setLanguage] = useState(initialLanguage || DEFAULT_AI_COPYWRITING_LANGUAGE);
  const [maxCharsInput, setMaxCharsInput] = useState('');
  const [uploadMode, setUploadMode] = useState(UPLOAD_MODE_DEFAULT);
  const [prompt, setPrompt] = useState('');

  const resetFilters = () => {
    setKeyword('');
    setCategory('all');
    setPlatform('all');
  };

  useEffect(() => {
    if (!isOpen) return;
    // 默认 Tab 跟随顶栏工作区模式；外部显式指定的 scope 仅在模式不可用时兜底
    const byMode = resolveDefaultTitlePresetScope(workspaceModeFromHook, canAccessTeamPresets);
    if (visibleScopes.some((item) => item.key === byMode)) {
      setScope(byMode);
    } else if (visibleScopes.some((item) => item.key === initialScope)) {
      setScope(initialScope);
    } else {
      setScope(DEFAULT_TITLE_PRESET_SCOPE);
    }
    const nextLang = AI_COPYWRITING_LANGUAGE_OPTIONS.some((o) => o.value === initialLanguage)
      ? initialLanguage
      : DEFAULT_AI_COPYWRITING_LANGUAGE;
    setLanguage(nextLang);
    setMaxCharsInput(String(initialMaxChars || '').trim());
    setUploadMode(isRemake ? UPLOAD_MODE_CUSTOM : UPLOAD_MODE_DEFAULT);
    setPrompt(String(initialPrompt || '').trim());
    resetFilters();
  }, [isOpen, initialScope, initialLanguage, initialMaxChars, initialPrompt, isRemake, visibleScopes, workspaceModeFromHook, canAccessTeamPresets]);

  useEffect(() => {
    if (!isOpen || confirming) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, confirming, onClose]);

  const scopePresets = useMemo(
    () => filterTitlePresetsByScope(presets, scope),
    [presets, scope],
  );

  const filteredPresets = useMemo(() => {
    const byCategory = filterTitlePresetsByCategory(scopePresets, category);
    const byPlatform = filterTitlePresetsByPlatform(byCategory, platform);
    return filterTitlePresetsByKeyword(byPlatform, keyword);
  }, [scopePresets, category, platform, keyword]);

  if (!isOpen) return null;

  const previewSources = Array.isArray(sources) ? sources : [];
  const resolvedCount = previewSources.length || Number(selectedCount) || 0;

  const modal = (
    <div className="ai-cw-tpl-picker-layer" role="presentation">
      <div
        className="ai-cw-tpl-picker-layer__backdrop"
        role="presentation"
        onClick={() => {
          if (!confirming) onClose?.();
        }}
      />
      <div
        className="ai-cw-tpl-picker"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ai-cw-tpl-picker-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="ai-cw-tpl-picker__header">
          <div className="ai-cw-tpl-picker__header-text">
            <h3 id="ai-cw-tpl-picker-title">{isRemake ? t('goodsStudioCreate.remakeTitle') : t('aiCwTemplatePicker.titlePick')}</h3>
            <p>
              {isRemake
                ? t('aiCwTemplatePicker.subtitleRemake', { count: resolvedCount })
                : generateOnConfirm
                  ? t('aiCwTemplatePicker.subtitleGenerate', { count: resolvedCount })
                  : t('aiCwTemplatePicker.subtitleBring', { count: resolvedCount })}
            </p>
          </div>
          <button
            type="button"
            className="ai-cw-tpl-picker__icon-btn"
            onClick={onClose}
            disabled={confirming}
            aria-label={t('commonActions.close')}
          >
            <X size={18} />
          </button>
        </header>

        <div className="ai-cw-tpl-picker__body">
          <div className="ai-cw-tpl-picker__main">
            <DetailToolBridgeSourcesPreview
              className="ai-cw-tpl-picker__sources"
              sources={previewSources}
              title={isRemake ? t('aiCwTemplatePicker.sourcesRemake') : t('detailToolBridge.sourcesTitle')}
            />
            <div className="ai-cw-tpl-picker__toolbar">
              <div className="ai-cw-tpl-picker__tabs" role="tablist" aria-label={t('aiCwTemplatePicker.tabsAria')}>
                {visibleScopes.map((item) => {
                  const count = filterTitlePresetsByScope(presets, item.key).length;
                  const active = scope === item.key;
                  return (
                    <button
                      key={item.key}
                      type="button"
                      role="tab"
                      className={`ai-cw-tpl-picker__tab${active ? ' is-active' : ''}`}
                      aria-selected={active}
                      disabled={confirming}
                      onClick={() => {
                        setScope(item.key);
                        resetFilters();
                      }}
                    >
                      {item.label}
                      <em>{count}</em>
                    </button>
                  );
                })}
              </div>

              <div className="ai-cw-tpl-picker__filters">
                <label className="ai-cw-tpl-picker__search">
                  <Search size={14} className="ai-cw-tpl-picker__search-icon" aria-hidden="true" />
                  <input
                    type="text"
                    className="ai-cw-tpl-picker__search-input"
                    role="searchbox"
                    value={keyword}
                    disabled={confirming}
                    onChange={(e) => setKeyword(e.target.value)}
                    placeholder={t('aiCwTemplatePicker.searchPh')}
                    aria-label={t('aiCwTemplatePicker.searchAria')}
                  />
                </label>
                <IpeToolbarSelect
                  id="ai-cw-tpl-picker-category"
                  className="ai-cw-tpl-picker__filter"
                  value={category}
                  onChange={setCategory}
                  options={categoryFilterOptions}
                  ariaLabel={t('aiCwTemplatePicker.filterCategoryAria')}
                  variant="light"
                  clearable={false}
                  disabled={confirming}
                  width={120}
                />
                <IpeToolbarSelect
                  id="ai-cw-tpl-picker-platform"
                  className="ai-cw-tpl-picker__filter"
                  value={platform}
                  onChange={setPlatform}
                  options={platformFilterOptions}
                  ariaLabel={t('aiCwTemplatePicker.filterPlatformAria')}
                  variant="light"
                  clearable={false}
                  disabled={confirming}
                  width={120}
                />
              </div>
            </div>

            <div className="ai-cw-tpl-picker__scroll" role="list">
              {filteredPresets.length === 0 ? (
                <div className="ai-cw-tpl-picker__empty" role="status">
                  <strong>
                    {scopePresets.length === 0
                      ? t('aiCwTemplatePicker.emptyCategory')
                      : t('aiCwTemplatePicker.emptyNoMatch')}
                  </strong>
                  <span>
                    {scopePresets.length === 0
                      ? t('aiCwTemplatePicker.emptyCategoryHint')
                      : t('aiCwTemplatePicker.emptyNoMatchHint')}
                  </span>
                </div>
              ) : (
                <div className="ai-cw-tpl-picker__grid">
                  {filteredPresets.map((preset) => (
                    <TemplatePickCard
                      key={preset.id}
                      preset={preset}
                      active={String(selectedId) === String(preset.id)}
                      disabled={confirming}
                      onSelect={(item) => {
                        onSelectId?.(String(item.id));
                        if (item?.content != null) {
                          setPrompt(String(item.content).slice(0, promptMaxLength));
                        }
                      }}
                      currentUserId={currentUserId}
                      ownerNameByUserId={ownerNameByUserId}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          <aside className="ai-cw-tpl-picker__aside" aria-label={t('aiCwTemplatePicker.asideAria')}>
            {!isRemake ? (
              <section className="ai-cw-tpl-picker__aside-block ai-copywriting-mode-tabs">
                <span className="ai-cw-tpl-picker__aside-title">{t('aiCwTemplatePicker.uploadMode')}</span>
                <div className="ai-copywriting-mode-tabs__row" role="group" aria-label={t('aiCwTemplatePicker.uploadMode')}>
                  <button
                    type="button"
                    className={`ai-copywriting-mode-tabs__btn${uploadMode === UPLOAD_MODE_DEFAULT ? ' is-active' : ''}`}
                    onClick={() => setUploadMode(UPLOAD_MODE_DEFAULT)}
                    disabled={confirming}
                    aria-pressed={uploadMode === UPLOAD_MODE_DEFAULT}
                  >
                    {t('aiCwTemplatePicker.uploadModeDefault')}
                  </button>
                  <button
                    type="button"
                    className={`ai-copywriting-mode-tabs__btn${uploadMode === UPLOAD_MODE_CUSTOM ? ' is-active' : ''}`}
                    onClick={() => setUploadMode(UPLOAD_MODE_CUSTOM)}
                    disabled={confirming}
                    aria-pressed={uploadMode === UPLOAD_MODE_CUSTOM}
                  >
                    {t('aiCwTemplatePicker.uploadModeCustom')}
                  </button>
                </div>
              </section>
            ) : null}

            <section className="ai-cw-tpl-picker__aside-block">
              <label className="ai-cw-tpl-picker__aside-title" htmlFor="ai-cw-tpl-picker-prompt">
                {promptLabel}
              </label>
              <div className="ai-cw-tpl-picker__prompt-wrap">
                <textarea
                  id="ai-cw-tpl-picker-prompt"
                  className="ai-cw-tpl-picker__prompt"
                  placeholder={promptPlaceholder}
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value.slice(0, promptMaxLength))}
                  maxLength={promptMaxLength}
                  disabled={confirming}
                />
                <span className="ai-cw-tpl-picker__prompt-count" aria-live="polite">
                  {prompt.length}/{promptMaxLength}
                </span>
              </div>
            </section>

            <section className="ai-cw-tpl-picker__aside-block">
              <div className="ai-cw-tpl-picker__aside-head">
                <h4 className="ai-cw-tpl-picker__aside-title">{t('aiCwTemplatePicker.outputLanguage')}</h4>
                <p className="ai-cw-tpl-picker__aside-desc">{t('aiCwTemplatePicker.outputLanguageDesc')}</p>
              </div>
              <IpeToolbarSelect
                id="ai-cw-tpl-picker-language"
                className="ai-cw-tpl-picker__lang-select"
                value={language}
                onChange={setLanguage}
                options={LANGUAGE_SELECT_OPTIONS}
                ariaLabel={t('aiCwTemplatePicker.outputLanguage')}
                variant="light"
                clearable={false}
                disabled={confirming}
                width="100%"
              />
            </section>

            <section className="ai-cw-tpl-picker__aside-block ai-cw-tpl-picker__chars">
              <div className="ai-cw-tpl-picker__aside-head">
                <h4 className="ai-cw-tpl-picker__aside-title">{t('aiCwTemplatePicker.maxChars')}</h4>
                <p className="ai-cw-tpl-picker__aside-desc">{t('aiCwTemplatePicker.maxCharsDesc')}</p>
              </div>
              <label className="ai-cw-tpl-picker__chars-field">
                <input
                  type="number"
                  className="ai-cw-tpl-picker__chars-input"
                  min={1}
                  max={AI_COPYWRITING_MAX_CHARS_LIMIT}
                  inputMode="numeric"
                  placeholder={t('aiCwTemplatePicker.maxCharsPh')}
                  value={maxCharsInput}
                  disabled={confirming}
                  aria-label={t('aiCwTemplatePicker.maxChars')}
                  onChange={(e) => setMaxCharsInput(sanitizeCopywritingMaxCharsInput(e.target.value))}
                />
                <span className="ai-cw-tpl-picker__chars-suffix">{t('aiCwTemplatePicker.maxCharsSuffix')}</span>
              </label>
            </section>
          </aside>
        </div>

        {typeof onGoManage === 'function' ? (
          <div className="ai-cw-tpl-picker__hint">
            <span>{t('aiCwTemplatePicker.noSuitable')}</span>
            <button
              type="button"
              disabled={confirming}
              onClick={() => onGoManage(scope)}
            >
              {t('aiCwTemplatePicker.goManage')}
            </button>
          </div>
        ) : null}

        <footer className="ai-cw-tpl-picker__footer">
          <IpeCreditCostHint
            navId="aiCopywriting"
            imageCount={Math.max(1, resolvedCount)}
            className="ai-cw-tpl-picker__credit"
          />
          <button
            type="button"
            className="ai-cw-tpl-picker__btn ai-cw-tpl-picker__btn--ghost"
            onClick={onClose}
            disabled={confirming}
          >
            {t('commonActions.cancel')}
          </button>
          <button
            type="button"
            className="ai-cw-tpl-picker__btn ai-cw-tpl-picker__btn--primary"
            onClick={() => onConfirm?.({
              language,
              maxChars: parseCopywritingMaxChars(maxCharsInput),
              prompt,
              uploadMode,
            })}
            disabled={confirming}
          >
            {confirming
              ? (willGenerateHere ? t('aiCwTemplatePicker.generating') : t('goodsStudioCreate.createConfirming'))
              : (willGenerateHere ? t('aiCwTemplatePicker.startGenerate') : t('goodsStudioCreate.confirmBring'))}
          </button>
        </footer>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
